import React from 'react';
import { useTranslation } from 'react-i18next';

const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 11 11" style={{ width: '100%', height: '100%' }}>
    <g transform="translate(1)">
      <path d="m-.5.5 10 10" fill="none" stroke="#222" strokeWidth="1.2" />
      <path d="m9.5.5-10 10" fill="none" stroke="#222" strokeWidth="1.2" />
    </g>
  </svg>
);

const DeleteIcon = ({ color = '#767676' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="19" fill={color} viewBox="0 0 16 19">
    <path d="M2.53 18.9q-.6 0-1.05-.46-.46-.45-.46-1.05V3.14H0v-1.5h4.7V.9h6.6v.75H16v1.5h-1.03V17.4q0 .6-.45 1.05t-1.04.45zM13.48 3.13H2.53V17.4h10.95zm-8.3 12.1h1.5V5.27h-1.5zm4.14 0h1.5V5.27h-1.5zm-6.8-12.1V17.4z" />
  </svg>
);

const DeleteButton = ({ onClick }) => {
  const [hover, setHover] = React.useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      aria-label="Delete"
      style={{
        border: 'none',
        background: 'none',
        cursor: 'pointer',
        padding: 0,
        marginInlineStart: 12,
        display: 'flex',
        flexShrink: 0,
      }}
    >
      <DeleteIcon color={hover ? '#f73131' : '#767676'} />
    </button>
  );
};

const StarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24">
    <defs>
      <linearGradient id="ssStarGrad" x1=".5" x2=".5" y2="1" gradientUnits="objectBoundingBox">
        <stop offset="0" stopColor="#1deb85" />
        <stop offset="1" stopColor="#28b16d" />
      </linearGradient>
    </defs>
    <path fill="url(#ssStarGrad)" d="M17 3H7a2 2 0 0 0-2 2v16l7-3 7 3V5a2 2 0 0 0-2-2" />
  </svg>
);

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0,0,0,.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1050,
};

const CloseButton = ({ onClick, style }) => {
  const [hover, setHover] = React.useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      aria-label="Close"
      style={{
        cursor: 'pointer',
        backgroundColor: hover ? '#cecece' : 'transparent',
        border: 'none',
        outline: 'none',
        borderRadius: '.4rem',
        width: 24,
        minWidth: 24,
        height: 24,
        minHeight: 24,
        padding: 5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...style,
      }}
    >
      <CloseIcon />
    </button>
  );
};

// Bold the "Save Search" phrase inside the (translated) empty-state description.
const renderEmptyDescription = (text) => {
  const parts = text.split(/(save\s*search)/i);
  return parts.map((part, i) =>
    /^save\s*search$/i.test(part) ? (
      <strong key={i} style={{ color: '#222', fontWeight: 'bolder' }}>
        Save Search
      </strong>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    ),
  );
};

const SavedSearchesModal = ({ open, onClose, savedSearches = [], onDelete, buildHref }) => {
  const { t } = useTranslation();
  if (!open) return null;
  const hasList = savedSearches?.length > 0;

  return (
    <div style={overlayStyle} onClick={onClose}>
      {hasList ? (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            width: 600,
            height: 520,
            borderRadius: 8,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#f5f5f5',
          }}
        >
          <div
            style={{
              height: 51,
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: '#fff',
            }}
          >
            <span style={{ fontSize: 18, color: '#222', fontWeight: 700 }}>
              {t('Saved searches')} ({savedSearches.length})
            </span>
            <CloseButton onClick={onClose} />
          </div>
          <div
            style={{
              flex: 1,
              backgroundColor: '#f5f5f5',
              padding: 16,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
            }}
          >
            {savedSearches.map((item) => (
              <div
                key={item?.id}
                style={{
                  backgroundColor: '#fff',
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                }}
              >
                <a
                  href={buildHref ? buildHref(item) : item?.url}
                  style={{
                    fontSize: 16,
                    color: '#222',
                    fontWeight: 300,
                    textDecoration: 'none',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {item?.name}
                </a>
                <DeleteButton onClick={() => onDelete && onDelete(item)} />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            width: 322,
            height: 285,
            borderRadius: 8,
            backgroundColor: '#fff',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
          }}
        >
          <CloseButton onClick={onClose} style={{ position: 'absolute', top: 10, insetInlineEnd: 10 }} />
          <StarIcon />
          <p
            style={{
              marginTop: 24,
              marginBottom: 16,
              fontSize: 20,
              fontWeight: 700,
              lineHeight: 1.5,
              color: '#222',
              maxWidth: 182,
              textAlign: 'center',
            }}
          >
            {t("You haven't saved any searches yet")}
          </p>
          <p style={{ margin: 0, color: '#767676', fontSize: 16, lineHeight: 1.5, maxWidth: 272, textAlign: 'center' }}>
            {renderEmptyDescription(
              t('Use the SAVE SEARCH button below the filters to bookmark your property preferences'),
            )}
          </p>
        </div>
      )}
    </div>
  );
};

export default SavedSearchesModal;
